import React, { useMemo } from "react"

import {
    COMMON_COVERAGE_CONFIG,
    CalculatorResultType,
    CoverageCheckResponseType,
} from "@common-shared/config/coverage.config"
import {
    FeatureFlag,
    FeatureFlagsExistenceLogicalOperator,
} from "@common-shared/config/featureFlags.config"
import { OneVerifyFlagType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { COMMON_ONE_VERIFY_DEMO_CONFIG } from "@common-shared/config/oneVerify/oneVerifyDemo.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { CHECKER_CONFIG } from "../../../config/checker.config"
import { FEATURE_FLAGS_CONFIG } from "../../../config/featureFlags.config"
import { GENERAL_CONFIG } from "../../../config/general.config"
import { AuthHelper } from "../../../helpers/auth.helper"
import { CoverageHistoryHelper } from "../../../helpers/coverageHistory.helper"
import { PermissionsHelper } from "../../../helpers/permissions.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import type { IDynamicCheckerFormInputData } from "../../../types/data.types"
import type { ICoverageResultBreakdownComponentProps } from "../../../types/props.types"
import { CoverageResultBreakdownDetailsComponent } from "./coverageResultBreakdownDetails.component"
import { CoverageResultSmartScanComponent } from "./coverageResultSmartScan.component"

import alertRedIcon from "@front-shared/assets/images/icons/alert-red.svg"
import closeRedIcon from "@front-shared/assets/images/icons/close-red-outlined.svg"

export const CoverageResultBreakdownComponent: React.FunctionComponent<
    ICoverageResultBreakdownComponentProps
> = props => {
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const isSmartScanFeatureFlagEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_SCAN_MANUAL_CHECK,
    )

    const isBulkChecksAvailable = useIsFeatureFlagEnabled(
        FEATURE_FLAGS_CONFIG.bulkCheckerFeatureFlags,
        FeatureFlagsExistenceLogicalOperator.OR,
    )

    const canRunBulkCoverageChecks = useMemo<boolean>(() => {
        return !!(
            PermissionsHelper.hasPermission(
                OneVerifyPermission.runCsvBulkCoverageChecks,
                selectedPracticeRole,
            ) && isBulkChecksAvailable
        )
    }, [selectedPracticeRole, isBulkChecksAvailable])

    const smartScanData = useMemo<IDynamicCheckerFormInputData | null>(() => {
        const {
            modalityCode,
            providerNpi,
            firstName,
            lastName,
            dob,
            cptCode,
            sessionCharge,
            customerPatientId,
            customerPatientType,
            customerPatientNextAppointmentDate,
            inNetwork,
        } = props.requestData

        const neededProviderNpi =
            providerNpi || selectedPracticeRole?.practice.groupNPI

        if (!firstName || !lastName || !dob || !neededProviderNpi) {
            return null
        }

        const resultZip = CoverageHistoryHelper.getSmartScanZipCode(
            props.coverageResult,
        )

        const useDemoZipFallback =
            AuthHelper.isDemoSessionActive() || GENERAL_CONFIG.isSandbox

        return {
            zipCode:
                resultZip ||
                (useDemoZipFallback
                    ? COMMON_ONE_VERIFY_DEMO_CONFIG.defaultDiscoverZip
                    : ""),

            id: UtilHelper.generateUniqueVarchar(100),
            modalityCode,
            firstName,
            lastName,
            dob,
            cptCode,
            providerNpi: neededProviderNpi,
            sessionRate: (
                UtilHelper.formatCents(sessionCharge) ||
                COMMON_COVERAGE_CONFIG.defaultHealthProviderRate
            ).toString(),
            inNetworkCheck:
                (typeof inNetwork === "undefined"
                    ? props.coverageResult.isInNetworkCheck
                    : inNetwork) ?? true,

            customerPatientId,
            customerPatientType,
            customerPatientNextAppointmentDate,
        }
    }, [
        selectedPracticeRole?.practice.id,
        props.requestData,
        props.coverageResult,
    ])

    const isScanResult = useMemo<boolean>(() => {
        return (
            !!props.coverageResult.isSmartScanResult ||
            props.coverageResult.responseType === CoverageCheckResponseType.SCAN
        )
    }, [
        props.coverageResult.isSmartScanResult,
        props.coverageResult.responseType,
    ])

    // With all needed permissions, data and only if wasn't already run
    const isSmartScanAvailable = useMemo<boolean>(() => {
        return !!(
            isSmartScanFeatureFlagEnabled &&
            canRunBulkCoverageChecks &&
            !isScanResult &&
            !!smartScanData
        )
    }, [
        canRunBulkCoverageChecks,
        isSmartScanFeatureFlagEnabled,
        isScanResult,
        smartScanData,
    ])

    const isActiveCoverage = useMemo<boolean>(() => {
        return CHECKER_CONFIG.activeCoverageResults.includes(
            props.coverageResult.resultType,
        )
    }, [props.coverageResult])

    const getResultTypeBasedBreakdown = (): JSX.Element => {
        if (isActiveCoverage || props.isOverridesModeActivated) {
            return (
                <CoverageResultBreakdownDetailsComponent
                    coverageResult={props.coverageResult}
                    coverageOverride={props.coverageOverride}
                    requestData={props.requestData}
                    requestPayer={props.requestPayer}
                    responsePayer={props.responsePayer}
                    requestedHealthProvider={props.requestedHealthProvider}
                    policy={props.policy}
                    isOverridesModeActivated={props.isOverridesModeActivated}
                    overridesFormKey={props.overridesFormKey}
                    isOverridesFormDisabled={props.isOverridesFormDisabled}
                    withFlagsSection={props.withFlagsSection}
                    agenticCheck={props.agenticCheck}
                    preAgenticCoverageResult={props.preAgenticCoverageResult}
                />
            )
        }

        // This will be CalculatorResultType.generalError
        let errorTitle: string | JSX.Element = "Something went wrong"
        let errorDescription: string | JSX.Element =
            "The cause of this error might be out of our control. We could not verify the eligibility for benefits at this time."
        let withSmartScan = false
        let alertIcon = alertRedIcon

        // check the different result type
        switch (props.coverageResult.resultType) {
            case CalculatorResultType.noCoverage:
                errorTitle = "Ineligible"
                errorDescription =
                    "This client does not have any active benefits."
                withSmartScan = true
                alertIcon = closeRedIcon

                break

            case CalculatorResultType.activePlanCoverageUnknown:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.activePlanCoverageUnknown
                    ]
                errorDescription =
                    "The plan is Active, however Nirvana is unable to determine if the plan covers benefits."

                break

            case CalculatorResultType.unknownPlanStatus:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.unknownPlanStatus
                    ]
                errorDescription =
                    "Nirvana is unable to determine if the plan is active or covers benefits at this time. Please contact the patient or payer directly. (Status Code: 400)"

                break

            case CalculatorResultType.payerNotSupported:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.payerNotSupported
                    ]
                errorDescription =
                    "Nirvana does not support coverage requests for this payer. (Status Code: 400)"

                break

            case CalculatorResultType.providerNotRecognized:
                // Note: in the backend we resolve providerNotRecognized into either ENROLLMENT_REQUIRED or CREDENTIALING_ERROR
                // based on the error message; if we don't match on the error message, we return REVIEW_REQUIRED.
                // While we could have created new `resultType`s for these scenarios, we opted to keep the same `resultType` for simplicity.
                // If neither of the expected flags are found, we will show the default providerNotRecognized error message that we showed formerly.
                if (
                    props.coverageResult.flags.includes(
                        OneVerifyFlagType.ENROLLMENT_REQUIRED,
                    )
                ) {
                    errorTitle = "Enrollment Required"
                    errorDescription =
                        "The payer does not have explicit enrollment with this NPI. Please contact Nirvana support to assist in enrolling this provider. (Status Code: 400)"
                } else if (
                    props.coverageResult.flags.includes(
                        OneVerifyFlagType.CREDENTIALING_ERROR,
                    )
                ) {
                    errorTitle = "Credentialing Error"
                    errorDescription =
                        "The payer does not recognize the provider NPI. Please contact the payer to resolve. (Status Code: 400)"
                } else {
                    errorTitle =
                        CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                            CalculatorResultType.providerNotRecognized
                        ]
                    errorDescription =
                        "The payer does not recognize the provider NPI - this may be a credentialing issue. Please try another check with a Custom NPI, or contact the payer to resolve. (Status Code: 400)"
                }

                break

            case CalculatorResultType.errorRetrievingCoverage:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.errorRetrievingCoverage
                    ]
                errorDescription =
                    "Nirvana is unable to retrieve coverage information for this plan. Please confirm that the payer and member information are correct and resubmit. (Status Code: 400)"

                break

            case CalculatorResultType.memberIdPayerIdInvalid:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.memberIdPayerIdInvalid
                    ]
                errorDescription =
                    "Nirvana previously located this member ID with a different payer.  Please retry with a different payer ID. (Status Code: 400)"

                break

            case CalculatorResultType.invalidInput:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.invalidInput
                    ]
                if (
                    props.coverageResult.coverageError &&
                    props.coverageResult.coverageError.message &&
                    props.coverageResult.coverageError.message
                        .toLowerCase()
                        .includes("demo")
                ) {
                    errorDescription = "Demo policy not allowed in production"
                } else {
                    errorDescription =
                        "An invalid data format was provided for a request parameter or the parameter was omitted. Details: validation_failures: “field x is a required field”. (Status Code: 422)"
                }
                break

            case CalculatorResultType.unexpectedError:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.unexpectedError
                    ]
                errorDescription =
                    "Nirvana is unable to retrieve coverage information at this time. Please try again and contact Nirvana if the error persists. (Status Code: 500)"

                break

            case CalculatorResultType.tooManyRequests:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.tooManyRequests
                    ]
                errorDescription =
                    "Nirvana is experiencing a high volume of requests. Please try again later. (Status Code: 429)"

                break

            case CalculatorResultType.payerFailure:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.payerFailure
                    ]
                errorDescription =
                    "The payer is currently experiencing downtime. Please try again later and contact Nirvana if the error persists. (Status Code: 500)"

                break

            case CalculatorResultType.memberNotFound:
                if (isSmartScanAvailable) {
                    withSmartScan = true

                    errorTitle = "No policy found with this payer"
                    errorDescription =
                        "Use a Nirvana Smart Scan™️ to recover policies."
                } else {
                    errorTitle =
                        CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                            CalculatorResultType.memberNotFound
                        ]

                    errorDescription = `${
                        isScanResult
                            ? "Unable to find active coverage for this member"
                            : "The payer does not recognize the member. Please confirm that the payer and member information are correct and resubmit"
                    }. (Status Code: 404)`
                }

                break

            case CalculatorResultType.connectionIssue:
                errorTitle =
                    CHECKER_CONFIG.calculatorResultTypeTitleMapping[
                        CalculatorResultType.connectionIssue
                    ]
                errorDescription = (
                    <>
                        The insurance plan is not responding at this time.
                        <br />
                        Please retry later.
                    </>
                )

                break
        }

        return (
            <div className="relative">
                <CoverageResultBreakdownDetailsComponent
                    coverageResult={props.coverageResult}
                    coverageOverride={props.coverageOverride}
                    requestData={props.requestData}
                    requestPayer={props.requestPayer}
                    responsePayer={props.responsePayer}
                    requestedHealthProvider={props.requestedHealthProvider}
                    policy={props.policy}
                    isErrorModeFields={true}
                    withFlagsSection={props.withFlagsSection}
                    agenticCheck={props.agenticCheck}
                    preAgenticCoverageResult={props.preAgenticCoverageResult}
                />

                <div className="py-[24px] mt-[24px]">
                    <div className="flex items-center justify-center">
                        <img className="mr-[8px]" src={alertIcon} alt="Error" />

                        <PrimaryText
                            className="relative top-[2px]"
                            typography="textSemibold"
                            size={18}
                        >
                            {errorTitle}
                        </PrimaryText>
                    </div>

                    <div className="mt-[8px] max-w-[550px] mx-auto">
                        <PrimaryText centered typography="text">
                            {errorDescription}
                        </PrimaryText>
                    </div>

                    {isSmartScanAvailable && withSmartScan && smartScanData ? (
                        <CoverageResultSmartScanComponent
                            notFloatingClassName="mt-[24px]"
                            marginClassName={
                                props.smartScanRightMarginClassName
                            }
                            isFloating={props.isSmartScanFloating}
                            currentRequestId={props.requestData.id}
                            smartScanData={smartScanData}
                            onSmartScanSuccess={props.onSmartScanSuccess}
                        />
                    ) : null}
                </div>
            </div>
        )
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            {getResultTypeBasedBreakdown()}
        </div>
    )
}
